import React, { useState } from 'react';
import { Mail, CheckCircle, XCircle, RefreshCw, AlertCircle } from 'lucide-react';
import { authService } from '../services/authService';
import { User } from '../types';

interface EmailVerificationModalProps {
  user: User;
  email: string;
  onVerified: (user: User) => void;
  onClose?: () => void;
}

export const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  user,
  email,
  onVerified,
  onClose
}) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!code || code.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const verifiedUser = await authService.verifyEmail(code, email);
      setSuccess(true);
      setTimeout(() => {
        onVerified(verifiedUser);
      }, 1500);
    } catch (err: any) {
      console.error('Verification error:', err);
      setError(err.message || 'Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setResending(true);
    try {
      await authService.resendVerification();
      setError('');
      alert('Verification code sent! Please check your email. If you don\'t see it, check your spam folder.');
    } catch (err: any) {
      console.error('Resend error:', err);
      let errorMsg = err.message || 'Failed to resend code. Please try again.';
      
      // Check if it's an email configuration error
      if (errorMsg.includes('refresh token') || errorMsg.includes('email configuration')) {
        errorMsg = 'Email service is not configured. Please contact support. The verification code was generated and saved - you can ask support for your code.';
      }
      
      setError(errorMsg);
    } finally {
      setResending(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-slate-800 rounded-2xl border border-emerald-500/50 p-8 max-w-md w-full text-center animate-fadeIn">
          <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4 animate-bounce" />
          <h2 className="text-2xl font-bold text-white mb-2">Email Verified! ✅</h2>
          <p className="text-slate-300 mb-4">
            Your email has been verified successfully. You now have 10 free credits!
          </p>
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
            <p className="text-emerald-400 font-semibold">Redirecting to dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 rounded-2xl border border-indigo-500/50 p-8 max-w-md w-full animate-fadeIn">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-indigo-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-indigo-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Verify Your Email</h2>
          <p className="text-slate-400 text-sm">
            We've sent a 6-digit verification code to
          </p>
          <p className="text-indigo-400 font-semibold mt-1">{email}</p>
        </div>

        <form onSubmit={handleVerify} className="space-y-4" method="post" action="#">
          <div>
            <label htmlFor="verification-code" className="block text-sm font-semibold text-slate-300 mb-2">
              Enter Verification Code
            </label>
            <input
              type="text"
              id="verification-code"
              name="verification-code"
              value={code}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setCode(value);
                setError('');
              }}
              placeholder="000000"
              className="w-full bg-slate-900 border border-slate-600 text-white text-center text-2xl font-mono tracking-widest p-4 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              maxLength={6}
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]{6}"
              autoFocus
            />
            <p className="text-xs text-slate-500 mt-2 text-center">
              Enter the 6-digit code from your email
            </p>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || code.length !== 6}
            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-3 rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Verifying...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                Verify Email
              </>
            )}
          </button>

          <div className="pt-4 border-t border-slate-700">
            <p className="text-sm text-slate-400 text-center mb-3">
              Didn't receive the code?
            </p>
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="w-full bg-slate-700 hover:bg-slate-600 text-white py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {resending ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Resend Code
                </>
              )}
            </button>
          </div>

          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="w-full mt-2 text-slate-400 hover:text-white text-sm transition-colors"
            >
              I'll verify later
            </button>
          )}
        </form>

        <div className="mt-6 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
          <p className="text-yellow-400 text-xs text-center">
            ⚠️ You must verify your email to receive your 10 free credits and access all features.
          </p>
        </div>
      </div>
    </div>
  );
};

